<template>
    <define-subtitle>
        <a :href="backUrl" class="no-underline! text-sky-600! text-base font-normal">
            <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
            All Users
        </a>
    </define-subtitle>

    <define-actions>
        <div class="flex flex-wrap items-end justify-end gap-2 font-normal text-base">
            <div class="min-w-[240px]">
                <vx-label>Team Role</vx-label>
                <vx-selectable
                    :modelValue="role"
                    @update:modelValue="onRoleChange"
                    :reduce="(option: RoleOption) => option.value"
                    :options="roleOptions"
                    :clearable="false"
                    :disabled="updatingRole"
                    size="lg"
                    data-test="role-select"
                    class="w-[240px]"
                />
            </div>
            <template v-if="inviteId">
                <vx-button
                    data-test="cancel-invite"
                    @click="cancelInvite"
                    color="muted-dark"
                    :loading="canceling"
                    size="lg"
                >
                    Cancel Invite <font-awesome-icon widthAuto :icon="faTrash" />
                </vx-button>
                <vx-button
                    data-test="resend"
                    @click="resendInvite"
                    color="muted-dark"
                    :loading="resending"
                    :disabled="outsideAllowedDomains"
                    size="lg"
                >
                    Re-send <font-awesome-icon widthAuto :icon="faPaperPlane" />
                </vx-button>
            </template>
        </div>
    </define-actions>

    <!-- Inside kiosk the team shell already provides the page layout, so only a header is rendered here. -->
    <page-header v-if="props.isKiosk" class="sm:flex-wrap!">
        <template v-slot:subtitle><reuse-subtitle /></template>
        {{ email }}
        <template v-slot:actions><reuse-actions /></template>
    </page-header>

    <component :is="props.isKiosk ? 'div' : VxPageLayout" :layout="props.isKiosk ? undefined : 'sidebar'">
        <template v-if="!props.isKiosk" v-slot:subtitle><reuse-subtitle /></template>
        <template v-if="!props.isKiosk" v-slot:title
            ><span class="break-all">{{ email }}</span></template
        >
        <template v-if="!props.isKiosk" v-slot:actions><reuse-actions /></template>
        <template v-if="!props.isKiosk" v-slot:sidebar>
            <page-sidebar appearance="nested" :links="sidebarLinks"></page-sidebar>
        </template>

        <div class="w-full">
            <vx-alert v-if="!emailSent" color="info" shadow class="mb-6" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faCircleInfo" />
                </template>

                <div class="py-2">
                    The invitation for <strong>{{ email }}</strong> has not been sent yet. Add at least one OU or Group
                    to complete the invitation.
                </div>
            </vx-alert>

            <vx-alert v-else-if="!blocked" color="success" shadow class="mb-6" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faCircleCheck" />
                </template>

                <div class="py-2">
                    The invitation has been sent to <strong>{{ email }}</strong
                    >. You can continue adding more OUs and Groups.
                </div>
            </vx-alert>

            <vx-alert v-if="blocked" color="danger" shadow class="mb-6" :closeable="false" data-test="invite-blocked">
                {{ blockedMessage }}
            </vx-alert>

            <vx-alert
                v-else-if="willUseSso"
                color="info"
                shadow
                class="mb-6"
                :closeable="false"
                data-test="sso-invite-hint"
            >
                They'll log in with SSO.
            </vx-alert>

            <vx-alert color="slate" shadow class="mb-6" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faFingerprint" />
                </template>

                <div class="flex gap-3 py-2 items-center">
                    <div class="shrink-0">
                        <strong>Access Summary</strong>
                    </div>
                    <div v-if="role === 'admin'" data-test="access-summary">
                        This invite is for a team
                        <strong>{{ TEAM_USER_ROLES[role] || '-' }}</strong>
                        and will have
                        <font-awesome-icon :icon="faCrown" class="text-amber-400 mr-0.5" />
                        <strong>Corporate</strong> access and access to
                        <font-awesome-icon :icon="faBuildingFlag" class="text-violet-500 mr-0.5" />
                        <strong>all OUs</strong> and
                        <font-awesome-icon :icon="faGrid2Plus" class="text-slate-500 mr-0.5" />
                        <strong>all Groups</strong>.
                    </div>
                    <div v-else data-test="access-summary">
                        This invite is for the team role
                        <strong>{{ TEAM_USER_ROLES[role] || '-' }}</strong>
                        and has
                        <template v-if="role !== 'none'">
                            <font-awesome-icon :icon="faCrown" class="text-amber-400 mr-0.5" />
                            <strong>Corporate</strong> access,
                        </template>
                        <font-awesome-icon :icon="faBuildingFlag" class="text-violet-500 mr-0.5" />
                        <strong>{{ ouCount }} {{ ouCount === 1 ? 'OU' : 'OUs' }}</strong>
                        and
                        <font-awesome-icon :icon="faGrid2Plus" class="text-slate-500 mr-0.5" />
                        <strong>{{ groupCount }} {{ groupCount === 1 ? 'Group' : 'Groups' }}</strong
                        >.
                    </div>
                </div>
            </vx-alert>

            <h2 class="text-2xl font-extrabold mb-6">Targets</h2>

            <page-controls>
                <template v-slot:left>
                    <div class="grow @3xl/controls:max-w-md">
                        <vx-filterable :active="filterActive" @toggle="filterActive = $event" data-test="filters">
                            <template v-slot:placeholder>
                                <span class="mr-2">Find</span>
                                <vx-badge
                                    v-if="filtersCount > 0"
                                    data-test="reset-filters"
                                    :borders="false"
                                    size="xs"
                                    @click.stop="resetFilters"
                                >
                                    {{ pluralized('filter', filtersCount, true) }}
                                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                                </vx-badge>
                            </template>
                            <template v-slot:form>
                                <form @submit.prevent="applyFilters" class="flex flex-col gap-4">
                                    <div>
                                        <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                            Keyword Search
                                        </filter-label>
                                        <vx-input
                                            type="text"
                                            data-test="filters-query"
                                            placeholder="Search"
                                            maxlength="191"
                                            size="lg"
                                            v-model="filters.query"
                                        >
                                            <template v-slot:right>
                                                <font-awesome-icon
                                                    widthAuto
                                                    class="text-xs"
                                                    :icon="faMagnifyingGlass"
                                                />
                                            </template>
                                        </vx-input>
                                    </div>

                                    <div class="flex gap-2 pt-4">
                                        <vx-button
                                            data-test="cancel-filters"
                                            type="button"
                                            color="muted-light"
                                            size="lg"
                                            @click="cancelFilters"
                                        >
                                            Cancel
                                        </vx-button>
                                        <vx-button data-test="filters-apply" type="submit" size="lg" block>
                                            Apply
                                            <font-awesome-icon widthAuto :icon="faCircleCheck" />
                                        </vx-button>
                                    </div>
                                </form>
                            </template>
                        </vx-filterable>
                    </div>
                    <div class="flex w-full gap-2 @xl/controls:w-auto">
                        <vx-button-group size="lg" class="w-full">
                            <vx-select
                                :modelValue="fetchParams.sort_field"
                                @update:modelValue="
                                    fetchParams.sort_field = String($event);
                                    loadData();
                                "
                                :options="sortOptions"
                            />
                            <vx-button
                                @click.prevent="
                                    fetchParams.sort_dir = fetchParams.sort_dir === 'asc' ? 'desc' : 'asc';
                                    loadData();
                                "
                                type="button"
                                color="white"
                                shadow
                                :aria-label="`Sort ${
                                    fetchParams.sort_dir === 'asc' ? 'ascending' : 'descending'
                                }. Press to toggle sort direction.`"
                            >
                                <font-awesome-icon
                                    widthAuto
                                    :icon="fetchParams.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                                />
                            </vx-button>
                        </vx-button-group>
                    </div>
                </template>

                <template v-slot:right>
                    <div class="flex gap-2">
                        <vx-button
                            size="lg"
                            data-test="add-to-ou"
                            color="violet"
                            :disabled="blocked"
                            @click="
                                addingOu = true;
                                addingGroup = false;
                            "
                        >
                            Add to OU
                            <font-awesome-icon widthAuto :icon="faBuildingFlag" />
                        </vx-button>
                        <vx-button
                            size="lg"
                            data-test="add-to-group"
                            color="muted-dark"
                            :disabled="blocked"
                            @click="
                                addingGroup = true;
                                addingOu = false;
                            "
                        >
                            Add to Group
                            <font-awesome-icon widthAuto :icon="faGrid2Plus" />
                        </vx-button>
                    </div>
                </template>
            </page-controls>

            <page-loading v-if="loadingTargets && !combinedList.length" />

            <template v-if="!loadingTargets && !combinedList.length && !addingOu && !addingGroup">
                <vx-box
                    v-if="filtersCount > 0"
                    color="info"
                    class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
                >
                    No results found.
                    <vx-button @click="resetFilters" data-test="empty-state-reset"> Reset Filters </vx-button>
                </vx-box>
                <vx-box v-else color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    No targets added yet. Add an OU or Group to send the invitation.
                </vx-box>
            </template>

            <vx-table
                v-if="combinedList.length > 0 || addingOu || addingGroup"
                class="xl:grid-cols-[1fr_auto_auto_auto]"
                rowBreakpoint="xl"
            >
                <vx-table-row isHeader>
                    <div>Name</div>
                    <div>Type</div>
                    <div class="flex items-center gap-1">
                        Role
                        <group-role-permissions-popover v-if="hasGroupTargets" />
                    </div>
                </vx-table-row>

                <invite-target-access-add-row
                    v-if="addingOu"
                    type="ou"
                    :teamId="teamId"
                    :saving="savingTarget"
                    @saved="onTargetAdded"
                    @cancel="addingOu = false"
                    data-test="add-ou-row"
                />

                <invite-target-access-add-row
                    v-if="addingGroup"
                    type="group"
                    :teamId="teamId"
                    :saving="savingTarget"
                    @saved="onTargetAdded"
                    @cancel="addingGroup = false"
                    data-test="add-group-row"
                />

                <invite-target-access-row
                    v-for="target in combinedList"
                    :key="`${target.type}-${target.targetId}`"
                    :teamId="teamId"
                    :inviteId="inviteId!"
                    :target="target"
                    :isKiosk="props.isKiosk"
                    @removed="onTargetRemoved"
                />
            </vx-table>

            <div v-if="combinedList.length" class="mt-8 flex justify-between w-full gap-10">
                <vx-button
                    v-if="hasMore"
                    @click="loadMore()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loadingTargets"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner" />
                </vx-button>
                <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                    {{ combinedList.length }} / {{ ouCount + groupCount }}
                </div>
            </div>
        </div>
    </component>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { isAxiosError } from 'axios';
import {
    VxAlert,
    VxBadge,
    VxBox,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxLabel,
    VxPageLayout,
    VxSelect,
    VxSelectable,
    VxSwal,
    VxTable,
    VxTableRow,
} from '@voxie/frontend-components';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import {
    faArrowDownWideShort,
    faArrowLeft,
    faArrowUpWideShort,
    faBuildingFlag,
    faCircleCheck,
    faCircleInfo,
    faCircleXmark,
    faCrown,
    faFingerprint,
    faGrid2Plus,
    faMagnifyingGlass,
    faPaperPlane,
    faSpinner,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import PageControls from '~/components/general/PageControls.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { createReusableTemplate } from '@vueuse/core';
import FilterLabel from '~/components/general/FilterLabel.vue';
import InviteTargetAccessRow from './InviteTargetAccessRow.vue';
import InviteTargetAccessAddRow from './InviteTargetAccessAddRow.vue';
import GroupRolePermissionsPopover from '~/components/settings/teams/groups/GroupRolePermissionsPopover.vue';
import { invitationsClient, orgUnitInvitationsClient, groupInvitationsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useInviteEmailDomains } from '~/composables/useInviteEmailDomains';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import type { CursorPaginationParams } from '~/services/BaseClient.types';
import {
    TEAM_USER_ROLES,
    ORG_UNIT_USER_ROLES,
    GROUP_USER_ROLES,
    teamRolesOptions,
    type RoleOption,
} from '~/constants/roles';

const roleOptions = teamRolesOptions({ exclude: ['owner'] });
import { pluralized } from '~/components/filters';
import type { InviteTarget } from './InviteTargetAccessRow.vue';

type TargetType = 'ou' | 'group';

interface TargetAccess {
    id?: number;
    group_id?: string;
    name: string;
    role: string;
}

const toasted = useToasted();

const props = defineProps<{
    teamId: number;
    inviteId: number | null;
    inviteEmail: string;
    inviteRole: string;
    isKiosk?: boolean;
}>();

const [DefineSubtitle, ReuseSubtitle] = createReusableTemplate();
const [DefineActions, ReuseActions] = createReusableTemplate();

const teamId = props.teamId;

const inviteId = ref<number | null>(props.inviteId);
const email = ref(props.inviteEmail || '');
const role = ref(props.inviteRole || 'none');
const ouCount = ref(0);
const groupCount = ref(0);
const updatingRole = ref(false);
const resending = ref(false);
const canceling = ref(false);
const emailSent = ref(props.inviteId !== null);

const { blocked, blockedMessage, outsideAllowedDomains, willUseSso } = useInviteEmailDomains(teamId, email);

const cancelInvite = () => {
    if (null === inviteId.value || canceling.value) {
        return;
    }

    VxSwal.fire({
        title: 'Cancel Invite',
        text: 'Are you sure? There is no undo.',
        icon: 'danger',
        showCancelButton: true,
        confirmButtonColor: 'danger',
        confirmButtonText: 'Cancel Invite',
        cancelButtonText: 'Keep Invite',
        preConfirm: () => {
            canceling.value = true;

            return invitationsClient
                .deleteInvite(teamId, inviteId.value!)
                .then(() => {
                    toasted.success(`${email.value} invite canceled`);
                    window.location.assign(backUrl.value);
                })
                .catch(throwSwalRequestError)
                .finally(() => {
                    canceling.value = false;
                });
        },
    });
};

const resendInvite = () => {
    if (null === inviteId.value || resending.value || outsideAllowedDomains.value) {
        return;
    }

    VxSwal.confirmation('Re-send Invite', 'Are you sure you want to re-send the invite?', 'success', () => {
        resending.value = true;

        return invitationsClient
            .resendInvite(teamId, inviteId.value!)
            .then(() => {
                toasted.success('Invitation re-sent successfully');
            })
            .catch(throwSwalRequestError)
            .finally(() => {
                resending.value = false;
            });
    });
};

const onRoleChange = async (newRole: string) => {
    if (newRole === role.value || updatingRole.value) {
        return;
    }

    const previousRole = role.value;
    role.value = newRole;

    if (null === inviteId.value) {
        return;
    }

    updatingRole.value = true;

    try {
        await invitationsClient.updateInvite(teamId, inviteId.value, { role: newRole });
        toasted.success(`Team role updated to ${roleOptions.find((o) => o.value === newRole)?.label || newRole}`);
    } catch (e) {
        role.value = previousRole;
        toasted.error(catchRequestError(e));
    } finally {
        updatingRole.value = false;
    }
};

const backUrl = computed(() =>
    props.isKiosk ? `/kiosk/teams/${teamId}/users` : `/settings/teams/${teamId}#/membership`
);

const inviteUrl = (id: number) =>
    props.isKiosk
        ? `/kiosk/teams/${teamId}/invites/${id}/target-access`
        : `/settings/teams/${teamId}/membership/invites/${id}/target-access`;

const createUrl = computed(() =>
    props.isKiosk ? `/kiosk/teams/${teamId}/invites/create` : `/settings/teams/${teamId}/membership/invites/create`
);

const sidebarLinks = computed(() => {
    const base = null !== inviteId.value ? inviteUrl(inviteId.value) : createUrl.value;

    return [{ href: base, label: 'Target Access' }];
});

const {
    list: ouList,
    loading: ouLoading,
    fetchParams: ouFetchParams,
    fetchRecords: fetchOuRecords,
    isLast: ouIsLast,
} = useCursorPagination<TargetAccess>();

const {
    list: groupList,
    loading: groupLoading,
    fetchParams: groupFetchParams,
    fetchRecords: fetchGroupRecords,
    isLast: groupIsLast,
} = useCursorPagination<TargetAccess>();

const loadingTargets = computed(() => ouLoading.value || groupLoading.value);
const hasMore = computed(() => !ouIsLast.value || !groupIsLast.value);

const addingOu = ref(false);
const addingGroup = ref(false);
const savingTarget = ref(false);
const filterActive = ref(false);

const filters = ref<CursorPaginationParams>({
    sort_dir: 'asc',
    sort_field: 'type',
});

const fetchParams = ref<CursorPaginationParams>({
    sort_dir: 'asc',
    sort_field: 'type',
});

watch(
    () => fetchParams.value,
    (value) => {
        filters.value = { ...value };
    },
    { deep: true, immediate: true }
);

const sortOptions = [
    { label: 'Sort: Type', value: 'type' },
    { label: 'Sort: Name', value: 'name' },
];

const filtersCount = computed(() => (fetchParams.value.query ? 1 : 0));

const recentlyAdded = ref<InviteTarget[]>([]);
const recentKeys = computed(() => new Set(recentlyAdded.value.map((t) => `${t.type}-${t.targetId}`)));

const hasGroupTargets = computed(() => addingGroup.value || groupList.value.length > 0);

const combinedList = computed((): InviteTarget[] => {
    const all: InviteTarget[] = [
        ...ouList.value.map((ou): InviteTarget => ({ name: ou.name, type: 'ou', role: ou.role, targetId: ou.id! })),
        ...groupList.value.map(
            (g): InviteTarget => ({ name: g.name, type: 'group', role: g.role, targetId: g.group_id! })
        ),
    ];

    const pinned = all.filter((t) => recentKeys.value.has(`${t.type}-${t.targetId}`));
    const rest = all.filter((t) => !recentKeys.value.has(`${t.type}-${t.targetId}`));

    const field = fetchParams.value.sort_field || 'type';
    const dir = fetchParams.value.sort_dir || 'asc';
    rest.sort((a, b) => {
        let cmp: number;
        if (field === 'type') {
            const typeOrder: Record<TargetType, number> = { ou: 0, group: 1 };
            cmp = typeOrder[a.type] - typeOrder[b.type] || a.name.localeCompare(b.name);
        } else {
            cmp = a.name.localeCompare(b.name);
        }
        return dir === 'asc' ? cmp : -cmp;
    });

    return [...pinned, ...rest];
});

const loadOUs = async () => {
    if (null === inviteId.value) {
        return;
    }

    ouFetchParams.value = {
        ...ouFetchParams.value,
        query: fetchParams.value.query || undefined,
        sort_dir: 'asc',
        per_page: 100,
    };
    await fetchOuRecords(invitationsClient.getInviteOrgUnits(teamId, inviteId.value, ouFetchParams.value));
    ouCount.value = ouList.value.length;
};

const loadGroups = async () => {
    if (null === inviteId.value) {
        return;
    }

    groupFetchParams.value = {
        ...groupFetchParams.value,
        query: fetchParams.value.query || undefined,
        sort_dir: 'asc',
        per_page: 100,
    };
    await fetchGroupRecords(invitationsClient.getInviteGroups(teamId, inviteId.value, groupFetchParams.value));
    groupCount.value = groupList.value.length;
};

const loadData = () => {
    recentlyAdded.value = [];
    ouFetchParams.value = { cursor: null };
    groupFetchParams.value = { cursor: null };
    loadOUs();
    loadGroups();
};

const loadMore = () => {
    if (!ouIsLast.value) {
        loadOUs();
    }
    if (!groupIsLast.value) {
        loadGroups();
    }
};

const applyFilters = async () => {
    filterActive.value = false;
    fetchParams.value = { ...filters.value };
    await nextTick();
    loadData();
};

const cancelFilters = () => {
    filters.value = { ...fetchParams.value };
    filterActive.value = false;
};

const resetFilters = () => {
    filterActive.value = false;
    fetchParams.value = {
        sort_dir: 'asc',
        sort_field: 'type',
    };
    loadData();
};

const onTargetAdded = async (target: { targetId: string | number; name: string; type: TargetType; role: string }) => {
    if (blocked.value) {
        return;
    }

    savingTarget.value = true;

    try {
        let response;
        if (target.type === 'ou') {
            response = await orgUnitInvitationsClient.sendInvitation(teamId, target.targetId, {
                email: email.value,
                role: target.role,
            });
        } else {
            response = await groupInvitationsClient.sendInvitation(teamId, target.targetId, {
                email: email.value,
                role: target.role,
            });
        }

        const returnedInviteId = response.data.id;

        if (null === inviteId.value) {
            inviteId.value = returnedInviteId;
            emailSent.value = true;
            sessionStorage.removeItem('voxie-pending-invite-email');
            sessionStorage.removeItem('voxie-pending-invite-role');

            history.replaceState(null, '', inviteUrl(returnedInviteId));
        }

        const list = target.type === 'ou' ? ouList : groupList;
        const idField = target.type === 'ou' ? 'id' : 'group_id';
        const existing = list.value.find((i) => i[idField] === target.targetId);

        if (existing) {
            existing.role = target.role;
        } else {
            list.value = [
                { [idField]: target.targetId, name: target.name, role: target.role } as TargetAccess,
                ...list.value,
            ];
            if (target.type === 'ou') {
                ouCount.value++;
            } else {
                groupCount.value++;
            }
        }

        recentlyAdded.value = [{ ...target } as InviteTarget, ...recentlyAdded.value];

        const rolesMap = target.type === 'ou' ? ORG_UNIT_USER_ROLES : GROUP_USER_ROLES;
        toasted.success(
            existing
                ? `${target.name} role updated to ${(rolesMap as Record<string, string>)[target.role]}`
                : `Added ${target.name} to invite`
        );
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        savingTarget.value = false;
        addingOu.value = false;
        addingGroup.value = false;
    }
};

// The list holds only the loaded targets that match the search.
const noTargetsLeft = async (id: number) => {
    try {
        const [orgUnits, groups] = await Promise.all([
            invitationsClient.getInviteOrgUnits(teamId, id, { per_page: 1 }),
            invitationsClient.getInviteGroups(teamId, id, { per_page: 1 }),
        ]);

        return 0 === orgUnits.data.data.length && 0 === groups.data.data.length;
    } catch (e) {
        // An invite with no team role is deleted together with its last target.
        if (isAxiosError(e) && HTTP_NOT_FOUND === e.response?.status) {
            toasted.success(`${email.value} invite canceled`);
            window.location.assign(backUrl.value);
        } else {
            toasted.error(catchRequestError(e));
        }

        return false;
    }
};

const onTargetRemoved = async (target: InviteTarget) => {
    const list = target.type === 'ou' ? ouList : groupList;
    const idField = target.type === 'ou' ? 'id' : 'group_id';
    list.value = list.value.filter((item) => item[idField] !== target.targetId);

    if (target.type === 'ou') {
        ouCount.value--;
    } else {
        groupCount.value--;
    }

    if (ouCount.value + groupCount.value === 0 && null !== inviteId.value && (await noTargetsLeft(inviteId.value))) {
        VxSwal.fire({
            title: 'No Targets Remaining',
            text: 'This invite has no targets left. Would you like to cancel the invite?',
            icon: 'danger',
            showCancelButton: true,
            confirmButtonColor: 'danger',
            confirmButtonText: 'Cancel Invite',
            cancelButtonText: 'Keep Invite',
            preConfirm: () => {
                return invitationsClient
                    .deleteInvite(teamId, inviteId.value!)
                    .then(() => {
                        toasted.success(`${email.value} invite canceled`);
                        window.location.assign(backUrl.value);
                    })
                    .catch(throwSwalRequestError);
            },
        });
    }
};

onMounted(() => {
    if (null === inviteId.value) {
        const storedEmail = sessionStorage.getItem('voxie-pending-invite-email') || '';

        if ('' === storedEmail) {
            toasted.error('No pending invite found. Please start the invite process again.');
            window.location.assign(backUrl.value);
            return;
        }

        email.value = storedEmail;
        role.value = sessionStorage.getItem('voxie-pending-invite-role') || 'none';
    } else {
        loadData();
    }
});
</script>
